import {
  BaseTextFieldProps,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { translate } from '@portal/services/i18n';
import React from 'react';

type InputPropsStyleType = {
  fontFamily: string;
  textSecurity?: string;
  InputSecurity?: string;
  WebkitTextSecurity?: string;
  MozTextSecurity?: string;
};

export type AdvancedInputProps = Omit<TextFieldProps, 'onChange'> & {
  onChange?: (value: string) => void;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  maxLength?: number;
  hideMaxLength?: boolean;
  readOnly?: boolean;
  securedTextField?: boolean;
  testId?: string;
  autoComplete?: BaseTextFieldProps['autoComplete'];
  hideVariantUnderline?: boolean;
  error?: boolean;
  errorMessage?: string;
};

const AdvancedInput = ({
  value,
  label,
  placeholder,
  variant = 'outlined',
  onChange,
  multiline = false,
  rows = multiline ? 4 : 1,
  type = 'text',
  InputLabelProps,
  disabled,
  endIcon,
  startIcon,
  hideVariantUnderline,
  maxLength,
  hideMaxLength = false,
  style,
  autoFocus,
  readOnly,
  tabIndex,
  testId = '',
  helperText,
  autoComplete,
  securedTextField = false,
  error,
  errorMessage,
  ...props
}: AdvancedInputProps) => {
  const { palette } = useTheme();

  const useStyles = makeStyles({
    root: {
      '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.primary.main,
      },
    },
    rootError: {
      '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: `${palette.error.main} !important`,
        borderWidth: '1px',
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: `${palette.error.main} !important`,
      },
      '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: `${palette.error.main} !important`,
        borderWidth: '2px',
      },
      '&:hover .MuiOutlinedInput-root fieldset': {
        borderColor: `${palette.error.main} !important`,
        borderWidth: '2px',
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${palette.error.main} !important`,
        borderWidth: '2px',
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: `${palette.error.main} !important`,
        borderWidth: '2px',
      },
      '& .MuiInputLabel-outlined': {
        color: `${palette.error.main} !important`,
      },
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 4,
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: 0,
      paddingRight: 0,
      minHeight: 20,
      lineHeight: 1.25,
    },
    errorText: {
      color: palette.error.main,
      fontSize: '0.75rem',
      lineHeight: 1.25,
      margin: 0,
      paddingLeft: 0,
      textAlign: 'left',
      flex: 1,
      minWidth: 0,
    },
    counterText: {
      color: palette.text.secondary,
      fontSize: '0.75rem',
      lineHeight: 1.25,
      margin: 0,
      marginLeft: 8,
      whiteSpace: 'nowrap' as const,
    },
    counterTextError: {
      color: palette.error.main,
    },
  });

  const classes = useStyles();

  const getHelperText = () => {
    if (maxLength && value && !hideMaxLength) {
      const valueLength = String(value).length;
      if (valueLength > maxLength) {
        return translate('APPLICATION.ERRORS.MAX_LENGTH_CARACTERES');
      }
      return `${valueLength} / ${maxLength}`;
    }

    if (helperText) {
      return helperText;
    }

    return undefined;
  };

  const isOverMaxLength =
    maxLength != null && value !== '' && String(value).length > maxLength;
  const hasError = error || isOverMaxLength;

  const counterLabel = (() => {
    if (hideMaxLength || maxLength == null || !value) return null;
    const valueLength = String(value).length;
    if (valueLength > maxLength) {
      return translate('APPLICATION.ERRORS.MAX_LENGTH_CARACTERES');
    }
    return `${valueLength} / ${maxLength}`;
  })();

  const rootClassName = hasError
    ? `${classes.root} ${classes.rootError}`
    : classes.root;

  const inputProps =
    variant !== 'outlined' && hideVariantUnderline
      ? {
          style: style,
          endAdornment: endIcon,
          startAdornment: startIcon,
          readOnly,
          disableUnderline: true,
        }
      : {
          style: style,
          endAdornment: endIcon,
          startAdornment: startIcon,
          readOnly,
        };

  const inputPropsStyle: InputPropsStyleType = {
    fontFamily: 'Inter, sans-serif',
  };

  if (securedTextField) {
    inputPropsStyle.textSecurity = 'disc';
    inputPropsStyle.InputSecurity = 'disc';
    inputPropsStyle.WebkitTextSecurity = 'disc';
    inputPropsStyle.MozTextSecurity = 'disc';
  }

  return (
    <div
      className="advanced-input"
      data-sentry-mask={
        securedTextField || type === 'password' ? true : undefined
      }
    >
      <TextField
        {...props}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className={rootClassName}
        inputProps={{
          style: inputPropsStyle,
          maxLength,
          tabIndex: tabIndex,
          testId,
        }}
        value={value || ''}
        label={label}
        placeholder={placeholder}
        variant={variant}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        fullWidth
        multiline={multiline}
        rows={rows}
        type={type}
        InputLabelProps={InputLabelProps}
        disabled={disabled}
        helperText={getHelperText()}
        InputProps={inputProps}
        error={hasError}
      />
      {errorMessage || counterLabel ? (
        <div className={classes.footer}>
          <span className={classes.errorText}>
            {hasError && errorMessage ? errorMessage : ''}
          </span>
          {counterLabel ? (
            <span
              className={`${classes.counterText}${
                isOverMaxLength ? ` ${classes.counterTextError}` : ''
              }`}
            >
              {counterLabel}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AdvancedInput;
